import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useCreateAutomation,
  useTestAutomation,
  useUpdateAutomation,
} from '@/hooks/use-automations';
import type {
  AutomationAction,
  AutomationCondition,
  AutomationItem,
  AutomationRule,
  AutomationTrigger,
  ConditionDraft,
} from '@hygge/shared';
import { AutomationRuleSchema, defaultConditionDraft } from '@hygge/shared';
import { ArrowRight, Check, Filter, Loader2, Pencil, Plus, Workflow, X, Zap } from 'lucide-react';
import { useExtensionsStore } from '@/stores/extensions';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { ActionCard } from './action-card';
import { AiPromptBar } from './ai-prompt-bar';
import { ConditionRow } from './condition-row';
import { FlowView } from './flow-view';
import { RunLog } from './run-log';
import { TriggerConfig } from './trigger-config';
import type { GenerateAutomationResult } from '@/hooks/use-automations';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface RuleBuilderProps {
  boardId: string;
  initial?: AutomationItem | (AutomationRule & { name?: string; description?: string });
  onClose: () => void;
  /** Which view to open in. Defaults to the editable form. */
  initialView?: 'edit' | 'flow';
}

// ─── Trigger type options ────────────────────────────────────────────────────

// v1: task-scoped triggers only; sprint lifecycle triggers return in Phase 2
// once sprint events + sprint-scoped actions exist.
const TRIGGER_TYPES = [
  { value: 'task.created', label: 'Task created' },
  { value: 'task.moved', label: 'Task moved' },
  { value: 'task.assigned', label: 'Task assigned' },
  { value: 'task.field_changed', label: 'Task field changed' },
  { value: 'comment.added', label: 'Comment added' },
  { value: 'task.due', label: 'Task due' },
] as const;

type TriggerType = (typeof TRIGGER_TYPES)[number]['value'];

// ─── Default trigger for each type ───────────────────────────────────────────

function defaultTrigger(type: TriggerType): AutomationTrigger {
  switch (type) {
    case 'task.created':
      return { type: 'task.created' };
    case 'task.moved':
      return { type: 'task.moved' };
    case 'task.assigned':
      return { type: 'task.assigned' };
    case 'task.field_changed':
      return { type: 'task.field_changed', field: 'priority' };
    case 'comment.added':
      return { type: 'comment.added' };
    case 'task.due':
      return { type: 'task.due', offset: '24h' };
  }
}

function defaultAction(): AutomationAction {
  return { type: 'set_field', field: 'priority', value: '' };
}

// ─── Resolve initial values ───────────────────────────────────────────────────

function resolveInitial(initial: RuleBuilderProps['initial']) {
  if (!initial) {
    return {
      name: '',
      enabled: true,
      trigger: { type: 'task.created' } as AutomationTrigger,
      match: 'all' as const,
      rules: [] as ConditionDraft[],
      actions: [] as AutomationAction[],
    };
  }
  return {
    name: initial.name ?? '',
    enabled: 'enabled' in initial ? initial.enabled : true,
    trigger: initial.trigger,
    match: initial.conditions?.match ?? 'all',
    // Cast existing saved conditions to ConditionDraft — they all have real fields
    rules: (initial.conditions?.rules ?? []) as ConditionDraft[],
    actions: initial.actions ?? [],
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function RuleBuilder({ boardId, initial, onClose, initialView }: RuleBuilderProps) {
  const existingId = initial && 'id' in initial ? initial.id : undefined;

  const init = resolveInitial(initial);
  const [name, setName] = useState(init.name);
  const [enabled, setEnabled] = useState(init.enabled);
  const [trigger, setTrigger] = useState<AutomationTrigger>(init.trigger);
  const [match, setMatch] = useState<'all' | 'any'>(init.match);
  const [conditions, setConditions] = useState<ConditionDraft[]>(init.rules);
  const [actions, setActions] = useState<AutomationAction[]>(
    init.actions.length > 0 ? init.actions : [defaultAction()]
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [view, setView] = useState<'edit' | 'flow'>(initialView ?? 'edit');
  // Describing a rule in words is the assistant's doing; the pickers below are
  // the product. With the extension off the builder is just the pickers.
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));

  // Test-on-task state (only when editing an existing rule)
  const [testTaskKey, setTestTaskKey] = useState('');
  const [testResult, setTestResult] = useState<{
    matched: boolean;
    wouldRun: { type: string }[];
  } | null>(null);

  const createMutation = useCreateAutomation(boardId);
  const updateMutation = useUpdateAutomation(boardId);
  const testMutation = useTestAutomation();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // ─── Trigger type switch ──────────────────────────────────────────────────

  function handleTriggerTypeChange(type: TriggerType) {
    setTrigger(defaultTrigger(type));
  }

  // ─── Conditions ───────────────────────────────────────────────────────────

  function addCondition() {
    setConditions((prev) => [...prev, defaultConditionDraft()]);
  }

  function updateCondition(idx: number, updated: ConditionDraft) {
    setConditions((prev) => prev.map((c, i) => (i === idx ? updated : c)));
  }

  function removeCondition(idx: number) {
    setConditions((prev) => prev.filter((_, i) => i !== idx));
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  function addAction() {
    setActions((prev) => [...prev, defaultAction()]);
  }

  function updateAction(idx: number, updated: AutomationAction) {
    setActions((prev) => prev.map((a, i) => (i === idx ? updated : a)));
  }

  function removeAction(idx: number) {
    setActions((prev) => prev.filter((_, i) => i !== idx));
  }

  // ─── Build the rule payload ───────────────────────────────────────────────
  // Pure + total: never throws on an in-progress rule. Drops condition drafts
  // whose field is still unset ('') so the schema parse + Flow view see only
  // real conditions. Used by both Save-gating and the Flow toggle.

  function buildRulePayload(): AutomationRule {
    const validConditions = conditions.filter((c): c is AutomationCondition => c.field !== '');
    return {
      trigger,
      conditions: { match, rules: validConditions },
      actions,
    };
  }

  // ─── Apply AI-generated rule ───────────────────────────────────────────────

  function applyGenerated(res: GenerateAutomationResult) {
    setTrigger(res.rule.trigger);
    setConditions(res.rule.conditions.rules as ConditionDraft[]);
    setMatch(res.rule.conditions.match);
    setActions(res.rule.actions);
    setWarnings(res.warnings);
    setView('edit');
  }

  // Live validation drives the Save-button gate + inline hint.
  // buildRulePayload() silently drops condition drafts with an unset field, so a
  // half-finished condition would otherwise save a BROADER rule than the screen
  // shows — block Save until every draft is completed or removed.
  const hasIncompleteConditions = conditions.some((c) => c.field === '');
  const parsedRule = AutomationRuleSchema.safeParse(buildRulePayload());
  const canSave = parsedRule.success && name.trim().length > 0 && !hasIncompleteConditions;

  // ─── Submit ───────────────────────────────────────────────────────────────

  function handleSave() {
    const errs: string[] = [];

    if (!name.trim()) {
      errs.push('Name is required');
    }

    if (hasIncompleteConditions) {
      errs.push('Complete or remove the condition without a field — it is not saved with the rule');
    }

    const rulePayload = buildRulePayload();

    const parsed = AutomationRuleSchema.safeParse(rulePayload);
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => errs.push(issue.message));
    }

    if (errs.length > 0) {
      setErrors(errs);
      return;
    }

    setErrors([]);

    const body = {
      name: name.trim(),
      description: undefined,
      enabled,
      ...rulePayload,
    };

    const onError = (err: unknown) => {
      const e = err as { response?: { data?: { message?: unknown } } };
      const raw = e.response?.data?.message;
      const items = Array.isArray(raw) ? raw : [raw];
      const msgs = items
        .map((m) => {
          if (typeof m === 'string') return m;
          const issue = m as { message?: string; path?: (string | number)[] };
          if (issue?.message) {
            const path = (issue.path ?? []).join('.');
            return path ? `${path}: ${issue.message}` : issue.message;
          }
          return JSON.stringify(m);
        })
        .filter(Boolean);
      setErrors(msgs.length > 0 ? msgs : ['Failed to save automation']);
    };

    if (existingId) {
      updateMutation.mutate({ id: existingId, body }, { onSuccess: onClose, onError });
    } else {
      createMutation.mutate(body, { onSuccess: onClose, onError });
    }
  }

  // ─── Test ─────────────────────────────────────────────────────────────────

  function handleTest() {
    if (!existingId || !testTaskKey.trim()) return;
    setTestResult(null);
    testMutation.mutate(
      { id: existingId, taskKey: testTaskKey.trim() },
      {
        onSuccess: (result) => setTestResult(result),
      }
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* ── Header: Name + enabled + Edit∣Flow toggle (visible in both views) ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="rule-name" className="mb-1.5 block">
              Rule name
            </Label>
            <Input
              id="rule-name"
              placeholder="e.g. Auto-assign when task is created"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <Switch checked={enabled} onCheckedChange={setEnabled} id="rule-enabled" />
            <Label htmlFor="rule-enabled" className="cursor-pointer">
              Enabled
            </Label>
          </div>
        </div>

        {/* Segmented control: Edit ∣ Flow */}
        <div
          role="group"
          aria-label="Builder view"
          className="inline-flex w-fit items-center gap-0.5 rounded-md bg-muted p-[3px]"
        >
          <button
            type="button"
            aria-pressed={view === 'edit'}
            onClick={() => setView('edit')}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              view === 'edit'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            type="button"
            aria-pressed={view === 'flow'}
            onClick={() => setView('flow')}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              view === 'flow'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Workflow className="h-3.5 w-3.5" />
            Flow
          </button>
        </div>
      </div>

      {view === 'flow' ? (
        <FlowView boardId={boardId} rule={buildRulePayload()} />
      ) : (
        <>
          {/* ── AI prompt bar ── */}
          {isAssistantEnabled && (
            <AiPromptBar
              boardId={boardId}
              currentRule={buildRulePayload()}
              onResult={applyGenerated}
            />
          )}

          {/* ── AI warnings banner ── */}
          {warnings.length > 0 && (
            <div className="relative rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
              <button
                type="button"
                className="absolute right-2 top-2 cursor-pointer rounded p-0.5 opacity-60 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => setWarnings([])}
                aria-label="Dismiss warnings"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="mb-1 font-medium">Some ids couldn't be matched — pick them manually:</p>
              <ul className="list-disc pl-4">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Section: When (trigger) ── */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
                <Zap className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-muted-foreground">When this happens</span>
            </div>
            <div className="ml-[2.375rem] border border-border rounded-md p-3 flex flex-col gap-0">
              <Select
                value={trigger.type}
                onValueChange={(val) => handleTriggerTypeChange(val as TriggerType)}
              >
                <SelectTrigger className="h-8 text-sm" aria-label="Trigger">
                  <SelectValue placeholder="Select trigger" />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <TriggerConfig boardId={boardId} trigger={trigger} onChange={setTrigger} />
            </div>
          </div>

          {/* ── Section: If (conditions) ── */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
                  <Filter className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-muted-foreground">If these match</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                Match
                <div
                  role="group"
                  aria-label="Match conditions"
                  className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5"
                >
                  {(['all', 'any'] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      aria-pressed={match === opt}
                      onClick={() => setMatch(opt)}
                      className={cn(
                        'cursor-pointer rounded px-2 py-0.5 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        match === opt
                          ? 'bg-hygge/10 text-hygge'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                conditions
              </div>
            </div>

            <div className="ml-[2.375rem] border border-border rounded-md p-3 flex flex-col gap-2">
              {conditions.length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  No conditions — rule runs for every matching trigger.
                </p>
              )}
              {conditions.map((cond, idx) => (
                <ConditionRow
                  key={idx}
                  boardId={boardId}
                  condition={cond}
                  onChange={(updated) => updateCondition(idx, updated)}
                  onRemove={() => removeCondition(idx)}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs self-start mt-1"
                onClick={addCondition}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add condition
              </Button>
            </div>
          </div>

          {/* ── Section: Then (actions) ── */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
                <ArrowRight className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-muted-foreground">Then do this</span>
            </div>
            <div className="ml-[2.375rem] border border-border rounded-md p-3 flex flex-col gap-3">
              {actions.map((action, idx) => (
                <ActionCard
                  key={idx}
                  boardId={boardId}
                  action={action}
                  onChange={(updated) => updateAction(idx, updated)}
                  onRemove={() => removeAction(idx)}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs self-start"
                onClick={addAction}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add action
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ── Validation errors (editor only) ── */}
      {view === 'edit' && errors.length > 0 && (
        <ul className="flex flex-col gap-1">
          {errors.map((err, i) => (
            <li key={i} className="text-xs text-destructive">
              {err}
            </li>
          ))}
        </ul>
      )}

      {/* ── Recent runs — only when editing an existing rule ── */}
      {view === 'edit' && existingId && (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold text-foreground">Recent runs</div>
          <RunLog automationId={existingId} />
        </div>
      )}

      {/* ── Footer ── */}
      <div className="flex flex-col gap-3 pt-1 border-t border-border">
        {/* Test on task — only when editing */}
        {view === 'edit' && existingId && (
          <div className="flex items-center gap-2">
            <Input
              className="h-8 text-sm flex-1"
              placeholder="Task key (e.g. PROJ-42)"
              value={testTaskKey}
              onChange={(e) => setTestTaskKey(e.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!testTaskKey.trim() || testMutation.isPending}
              onClick={handleTest}
            >
              {testMutation.isPending ? 'Testing…' : 'Test'}
            </Button>
            {testResult && (
              <span className="text-xs text-muted-foreground">
                {testResult.matched ? (
                  <span className="text-green-600">
                    Matched — {testResult.wouldRun.length} action(s) would run
                  </span>
                ) : (
                  <span className="text-muted-foreground">No match</span>
                )}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          {!canSave && (
            <span className="text-xs text-muted-foreground">
              {hasIncompleteConditions
                ? 'Complete or remove the empty condition'
                : 'Finish the highlighted fields'}
            </span>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={onClose}
              disabled={isPending}
              aria-label="Cancel"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              className="rounded-full bg-cta text-cta-foreground hover:bg-cta/90"
              onClick={handleSave}
              disabled={!canSave || isPending}
              aria-label={existingId ? 'Save changes' : 'Create rule'}
              title={existingId ? 'Save changes' : 'Create rule'}
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
